// Thin typed fetch wrapper. The session lives in an httponly cookie, so requests only
// need `credentials: same-origin` (Vite proxies /api in dev; FastAPI serves the bundle
// in production — one origin either way). Every request is JSON: the server rejects
// mutations that aren't, which is part of its CSRF defence.

import type {
  Appointment, AppointmentsResponse, Attachment, Automation, CalendarStatus,
  CallingQueue, CallingStatus, CallResult,
  ContactRole, ContactRolesResponse, ContactDetail, ContactPatch, CustomizeResponse, DealCondition, Drip, DripAudience, DripJourney, DripPace, DripPayload, DripPreview, Enrollment, EnrollmentDetail, Goals, ImportResult, InboxMessage, LeadBoard,
  LeadFlag,
  LeadList, LeadStage, Loan, LosExportPreview, LosExportRequest, LosExportResult, MailCopyTurn, MailDesign, MailDesignsResponse, MailDraftResult, MailPreview, MailProof, MailSendResult, MessagingStatus, MoveResult, NewAppointment, NewContact, NewDeal, Note, Officer, Option, Partner, PartnerReportRow, Person, PersonDetail, PipelineResponse, Playbook, Prefs, ProfilePatch, Relationship, RunningItem, SearchResults, SendResult, SourceRow, Subject, Summary, Task, TimelineItem, TodayResponse, User, Vocabulary, WorkflowDraft, WorkflowDraftResult, WorkflowsResponse,
} from './types'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** On a 409 duplicate: the id of the record that already exists. */
    readonly existingId?: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
  /** 401 means "sign in", not "something broke" — the shell branches on this. */
  get isAuth() {
    return this.status === 401
  }
  get isForbidden() {
    return this.status === 403
  }
  get isConflict() {
    return this.status === 409
  }
}

/** A message fit to show a user, whatever was thrown. */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Something went wrong.'
}

async function request<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method: init.method ?? 'GET',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    })
  } catch {
    // No response at all: offline, or the CRM was restarting for an update. Both
    // clear up on their own, so say that rather than something developer-shaped.
    throw new ApiError(0, 'Couldn’t reach the CRM — check your connection and try again in a moment.')
  }
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`
    let existingId: number | undefined
    try {
      const body = await res.json()
      if (typeof body?.detail === 'string') detail = body.detail
      if (typeof body?.existing_id === 'number') existingId = body.existing_id
    } catch {
      /* non-JSON error body — keep the status line */
    }
    throw new ApiError(res.status, detail, existingId)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/** A POST whose answer is a file, saved through the browser's own download. Shares
 *  `request`'s error handling so a refusal reads the same as everywhere else. */
async function download(path: string, body: unknown, fallbackName: string): Promise<void> {
  let res: Response
  try {
    res = await fetch(path, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    })
  } catch {
    throw new ApiError(0, 'Couldn’t reach the CRM — check your connection and try again in a moment.')
  }
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`
    try {
      const err = await res.json()
      if (typeof err?.detail === 'string') detail = err.detail
    } catch { /* non-JSON error body — keep the status line */ }
    throw new ApiError(res.status, detail)
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1]
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = name ?? fallbackName
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

type Params = Record<string, string | number | boolean | undefined | null>

const qs = (params: Params) => {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

const enc = encodeURIComponent

export const api = {
  // session
  me: () => request<User>('/api/me'),
  login: (email: string, password: string) =>
    request<User>('/api/login', { method: 'POST', body: { email, password } }),
  logout: () => request<{ ok: boolean }>('/api/logout', { method: 'POST', body: {} }),

  // home & settings
  summary: () => request<Summary>('/api/summary'),
  today: (limit = 5) => request<TodayResponse>(`/api/today${qs({ limit })}`),
  customize: () => request<CustomizeResponse>('/api/customize'),
  prefs: () => request<Prefs>('/api/prefs'),
  setPref: <K extends keyof Prefs>(key: K, value: Prefs[K]) =>
    request<{ key: K; value: Prefs[K] }>(`/api/prefs/${key}`, { method: 'PUT', body: { value } }),
  resetPref: (key: keyof Prefs) =>
    request<{ ok: boolean }>(`/api/prefs/${key}`, { method: 'DELETE', body: {} }),
  officers: () => request<{ officers: Officer[] }>('/api/officers'),
  search: (q: string) => request<SearchResults>(`/api/search${qs({ q })}`),
  inbox: () => request<{ messages: InboxMessage[] }>('/api/inbox'),

  // pipeline & loans
  pipeline: (includeTerminal = false) =>
    request<PipelineResponse>(`/api/pipeline${qs({ include_terminal: includeTerminal })}`),
  loan: (id: string) => request<Loan>(`/api/loans/${enc(id)}`),
  moveStage: (loanId: string, stage: string) =>
    request<MoveResult>(`/api/loans/${enc(loanId)}/stage`, { method: 'POST', body: { stage } }),
  createDeal: (deal: NewDeal) =>
    request<{ id: number; loan_id: string }>('/api/deals', { method: 'POST', body: deal }),
  updateDeal: (id: number, patch: NewDeal) =>
    request<Loan>(`/api/deals/${id}`, { method: 'PATCH', body: patch }),
  addCondition: (dealId: number, condition: { name: string; notes?: string }) =>
    request<{ conditions: DealCondition[] }>(`/api/deals/${dealId}/conditions`, { method: 'POST', body: condition }),
  updateCondition: (id: number, patch: { status?: string; name?: string; notes?: string }) =>
    request<{ conditions: DealCondition[] }>(`/api/conditions/${id}`, { method: 'PATCH', body: patch }),
  deleteCondition: (id: number) =>
    request<{ conditions: DealCondition[] }>(`/api/conditions/${id}`, { method: 'DELETE', body: {} }),

  // automations — "when X happens, start playbook Y"
  automations: () => request<{ automations: Automation[] }>('/api/automations'),
  setAutomation: (rule: { event: string; playbook: string; scope?: 'mine' | 'branch'; filter?: Record<string, string> | null; active?: boolean }) =>
    request<{ automations: Automation[] }>('/api/automations', { method: 'POST', body: rule }),
  deleteAutomation: (id: number) =>
    request<{ automations: Automation[] }>(`/api/automations/${id}`, { method: 'DELETE', body: {} }),
  sourceReport: (days = 365) =>
    request<{ sources: SourceRow[]; days: number }>(`/api/reports/sources${qs({ days })}`),

  // people & contacts
  people: (opts: { q?: string; role?: string; contact_role?: string; book?: 'leads' | 'mine'
                   /** The people behind the New contacts goal — added by you, in a window. */
                   added_by_me?: boolean; added_from?: string; added_to?: string } = {}) =>
    request<{ people: Person[]; books: Record<string, number>; role_counts: Record<string, number> }>(`/api/people${qs(opts)}`),
  person: (key: string) => request<PersonDetail>(`/api/people/${enc(key)}`),
  /** The lead-book contact for someone on a loan file, created if needed. */
  personToContact: (key: string) =>
    request<{ contact_id: number }>(`/api/people/${enc(key)}/contact`, { method: 'POST', body: {} }),
  contact: (id: number) => request<ContactDetail>(`/api/contacts/${id}`),
  createContact: (c: NewContact) =>
    request<{ id: number }>('/api/contacts', { method: 'POST', body: c }),
  updateContact: (id: number, patch: ContactPatch) =>
    request<ContactDetail>(`/api/contacts/${id}`, { method: 'PATCH', body: patch }),
  addMethod: (id: number, m: { kind: 'mobile' | 'landline' | 'email'; value: string; is_primary?: boolean }) =>
    request<ContactDetail>(`/api/contacts/${id}/methods`, { method: 'POST', body: m }),
  /** Removes them and everything hanging off them. Admin only; see writes.delete_contact. */
  deleteContact: (id: number) =>
    request<{ deleted: number; messages: number; calls: number; activity: number }>(
      `/api/contacts/${id}`, { method: 'DELETE' }),
  recordConsent: (id: number, c: { channel: string; status: 'opted_in' | 'opted_out' | 'none'; basis?: string; note?: string }) =>
    request<ContactDetail>(`/api/contacts/${id}/consent`, { method: 'POST', body: c }),
  /** The intake answers. A partial patch leaves unlisted fields untouched. */
  saveProfile: (id: number, profile: ProfilePatch) =>
    request<ContactDetail>(`/api/contacts/${id}/profile`, { method: 'PUT', body: profile }),
  vocabulary: () => request<Vocabulary>('/api/vocabulary'),
  importContacts: (csv: string) =>
    request<ImportResult>('/api/contacts/import', { method: 'POST', body: { csv } }),
  /** An .xlsx workbook, base64'd so the JSON-only API stays JSON-only. */
  importContactsXlsx: (xlsx_base64: string) =>
    request<ImportResult>('/api/contacts/import', { method: 'POST', body: { xlsx_base64 } }),
  /** Downloads through the browser so the session cookie does the authenticating. */
  exportContactsUrl: (fmt: 'xlsx' | 'csv', everyone = false) => `/api/contacts/export${qs({ fmt, everyone })}`,

  // handing a lead or an opportunity to LendingPad as a MISMO 3.4 file
  losExportPreview: (body: LosExportRequest) =>
    request<LosExportPreview>('/api/los-export/preview', { method: 'POST', body }),
  losExportFile: (body: LosExportRequest) =>
    download('/api/los-export/file', body, 'urla34.xml'),
  losExportPush: (body: LosExportRequest & { update?: boolean }) =>
    request<LosExportResult>('/api/los-export/push', { method: 'POST', body }),

  // mail pieces an officer designs and sends through Lob
  mailDesigns: () => request<MailDesignsResponse>('/api/mail/designs'),
  createMailDesign: (body: Partial<MailDesign> & { pdf_base64?: string; pdf_name?: string }) =>
    request<MailDesign>('/api/mail/designs', { method: 'POST', body }),
  updateMailDesign: (id: number, body: Partial<MailDesign> & { pdf_base64?: string; pdf_name?: string }) =>
    request<MailDesign>(`/api/mail/designs/${id}`, { method: 'PUT', body }),
  archiveMailDesign: (id: number) =>
    request<void>(`/api/mail/designs/${id}`, { method: 'DELETE' }),
  previewMailDesign: (body: Partial<MailDesign> & { sample_contact_id?: number }) =>
    request<MailPreview>('/api/mail/preview', { method: 'POST', body }),
  proofMailDesign: (id: number, contact_id?: number) =>
    request<MailProof>(`/api/mail/designs/${id}/proof`, { method: 'POST', body: { contact_id } }),
  sendMailDesign: (id: number, contact_ids: number[], live = false) =>
    request<MailSendResult>(`/api/mail/designs/${id}/send`, { method: 'POST', body: { contact_ids, live } }),
  draftMailCopy: (body: {
    instruction: string
    template_key: string
    fields: Record<string, string>
    history: MailCopyTurn[]
    layout?: string | null
    color?: string | null
  }) => request<MailDraftResult>('/api/mail/draft', { method: 'POST', body }),

  // messaging — the server enforces consent, DNC and quiet hours; the UI only explains
  /** What the composer opens with: the lead's own figures, in the officer's voice. */
  messageDraft: (contactId: number, channel: 'sms' | 'email') =>
    request<{ subject: string; body: string }>(`/api/contacts/${contactId}/draft/${channel}`),
  messagingStatus: () => request<MessagingStatus>('/api/messaging/status'),
  sendSms: (id: number, body: string, context: Partial<Record<'loan_id' | 'person_key', string>> = {}) =>
    request<SendResult>(`/api/contacts/${id}/sms`, { method: 'POST', body: { body, ...context } }),
  sendEmail: (id: number, subject: string, body: string, context: Partial<Record<'loan_id' | 'person_key', string>> = {}) =>
    request<SendResult>(`/api/contacts/${id}/email`, { method: 'POST', body: { subject, body, ...context } }),

  // leads
  leads: (campaign?: string, flag?: LeadFlag) =>
    request<LeadList>(`/api/leads${qs({ campaign, flag })}`),
  assignSelected: (contactIds: number[], loId: number) =>
    request<{ assigned: number; officer: string; unchanged: number }>(
      '/api/leads/assign-selected', { method: 'POST', body: { contact_ids: contactIds, lo_id: loId } }),
  /** The status board. It is about the officer's own contacts, so that is its default. */
  board: (book: 'leads' | 'mine' = 'mine', campaign?: string) =>
    request<LeadBoard>(`/api/board${qs({ campaign, book })}`),
  // status board lanes — the standard seven plus any the branch added
  // notes that stay put, and files on a record
  /** A photo of a business card → fields for the form. Nothing is saved server-side. */
  scanBusinessCard: (image_base64: string, content_type: string) =>
    request<{ fields: Record<string, string> }>('/api/contacts/scan-card',
      { method: 'POST', body: { image_base64, content_type } }),
  // who a contact knows — their realtor, the title officer, a co-borrower
  relationships: (contactId: number) =>
    request<{ relationships: Relationship[]; roles: Option[] }>(`/api/contacts/${contactId}/relationships`),
  addRelationship: (contactId: number, link: { role: string; other_contact_id?: number; other_person_key?: string; note?: string }) =>
    request<{ id: number }>(`/api/contacts/${contactId}/relationships`, { method: 'POST', body: link }),
  deleteRelationship: (id: number) => request<{ ok: true }>(`/api/relationships/${id}`, { method: 'DELETE' }),
  // appointments, and the Google Calendar they mirror to
  /** Pull Google now instead of waiting for the poller. Cheap: one incremental
   *  request, and nothing at all for an officer with no calendar connected. */
  pullCalendar: () => request<{ applied: number; skipped: number; pruned: number }>('/api/calendar/pull', { method: 'POST', body: {} }),
  appointments: (params: { start?: string; end?: string; contact_id?: number; person_key?: string; loan_id?: string; deal_id?: number } = {}) =>
    request<AppointmentsResponse>(`/api/appointments${qs(params)}`),
  createAppointment: (body: NewAppointment) =>
    request<Appointment>('/api/appointments', { method: 'POST', body }),
  updateAppointment: (id: number, patch: Partial<NewAppointment> & { status?: string }) =>
    request<Appointment>(`/api/appointments/${id}`, { method: 'PATCH', body: patch }),
  deleteAppointment: (id: number) =>
    request<{ ok: true }>(`/api/appointments/${id}`, { method: 'DELETE' }),
  googleStatus: () => request<CalendarStatus>('/api/google/status'),
  /** The consent URL to send the browser to. A click, not a redirect we perform. */
  googleConnect: () => request<{ url: string }>('/api/google/connect'),
  googleDisconnect: () => request<CalendarStatus>('/api/google/disconnect', { method: 'POST' }),
  googleSync: () => request<CalendarStatus & { applied: number }>('/api/google/sync', { method: 'POST' }),

  // what a contact is, and what the CRM may do with one
  contactRoles: () => request<ContactRolesResponse>('/api/contact-roles'),
  addContactRole: (body: { label: string; entity_kind?: 'person' | 'company' }) =>
    request<ContactRole>('/api/contact-roles', { method: 'POST', body }),
  editContactRole: (key: string, patch: Partial<ContactRole>) =>
    request<ContactRole>(`/api/contact-roles/${enc(key)}`, { method: 'PATCH', body: patch }),

  // monthly goals, counted from work that already happened
  goals: () => request<Goals>('/api/goals'),
  setGoals: (goals: Record<string, number>, period: string) =>
    request<Goals>('/api/goals', { method: 'PUT', body: { goals, period } }),
  notes: (subject: Subject) => request<{ notes: Note[] }>(`/api/notes${qs(subject as Params)}`),
  addNote: (subject: Subject, body: string) =>
    request<{ id: number }>('/api/notes', { method: 'POST', body: { ...subject, body } }),
  updateNote: (id: number, patch: { body?: string; pinned?: boolean }) =>
    request<{ ok: true }>(`/api/notes/${id}`, { method: 'PATCH', body: patch }),
  deleteNote: (id: number) => request<{ ok: true }>(`/api/notes/${id}`, { method: 'DELETE' }),
  attachments: (subject: Subject) =>
    request<{ attachments: Attachment[] }>(`/api/attachments${qs(subject as Params)}`),
  addAttachment: (subject: Subject, file: { filename: string; content_type: string; data_base64: string; is_card?: boolean }) =>
    request<Attachment>('/api/attachments', { method: 'POST', body: { ...subject, ...file } }),
  setBusinessCard: (id: number, is_card: boolean) =>
    request<{ ok: true }>(`/api/attachments/${id}`, { method: 'PATCH', body: { is_card } }),
  deleteAttachment: (id: number) => request<{ ok: true }>(`/api/attachments/${id}`, { method: 'DELETE' }),
  attachmentUrl: (id: number) => `/api/attachments/${id}`,
  /** How this officer arranges their board: lane order, and lanes they removed. */
  setLeadStagePrefs: (value: { order?: Record<string, number>; removed?: string[] }) =>
    request<{ key: string; value: { order: Record<string, number>; removed: string[] } }>(
      '/api/prefs/lead_stages', { method: 'PUT', body: { value } }),
  leadStagePrefs: () =>
    request<{ lead_stages: { order: Record<string, number>; removed: string[] } }>('/api/prefs'),
  /** The order of one column's cards after a drag. */
  setCardOrder: (contact_ids: number[]) =>
    request<{ ok: true; ordered: number }>('/api/leads/order', { method: 'POST', body: { contact_ids } }),
  leadStages: () => request<{ stages: LeadStage[] }>('/api/lead-stages'),
  addLeadStage: (label: string) =>
    request<{ stages: LeadStage[] }>('/api/lead-stages', { method: 'POST', body: { label } }),
  renameLeadStage: (key: string, label: string) =>
    request<{ stages: LeadStage[] }>(`/api/lead-stages/${encodeURIComponent(key)}`, { method: 'PATCH', body: { label } }),
  deleteLeadStage: (key: string) =>
    request<{ stages: LeadStage[] }>(`/api/lead-stages/${encodeURIComponent(key)}`, { method: 'DELETE' }),
  campaigns: () => request<{ campaigns: { campaign: string; leads: number }[] }>('/api/campaigns'),

  // activity, tasks, tags
  activity: (subject: Subject) =>
    request<{ items: TimelineItem[] }>(`/api/activity${qs(subject as Params)}`),
  logActivity: (subject: Subject, entry: { kind: 'note' | 'call'; body?: string; outcome?: string }) =>
    request<{ id: number }>('/api/activity', { method: 'POST', body: { ...subject, ...entry } }),
  tasks: (opts: { scope?: 'mine' | 'all'; status?: 'open' | 'done' | 'all'; subject?: Subject } = {}) =>
    request<{ tasks: Task[] }>(
      `/api/tasks${qs({ scope: opts.scope, status: opts.status, ...(opts.subject as Params) })}`),
  createTask: (t: { title: string; due?: string; notes?: string; assignee_lo_id?: number; subject?: Subject }) =>
    request<{ id: number }>('/api/tasks', {
      method: 'POST',
      body: { title: t.title, due: t.due, notes: t.notes, assignee_lo_id: t.assignee_lo_id, ...t.subject },
    }),
  updateTask: (id: number, patch: { done?: boolean; title?: string; due?: string | null; notes?: string; assignee_lo_id?: number }) =>
    request<{ ok: boolean }>(`/api/tasks/${id}`, { method: 'PATCH', body: patch }),
  tags: () => request<{ tags: { tag: string; uses: number }[] }>('/api/tags'),
  addTag: (subject: Subject, tag: string) =>
    request<{ tags: string[] }>('/api/tags', { method: 'POST', body: { ...subject, tag } }),
  removeTag: (subject: Subject, tag: string) =>
    request<{ tags: string[] }>('/api/tags', { method: 'DELETE', body: { ...subject, tag } }),

  // playbooks
  playbooks: () => request<{ playbooks: Playbook[]; messaging: MessagingStatus }>('/api/playbooks'),
  enrollments: (subject: Subject) =>
    request<{ enrollments: Enrollment[] }>(`/api/enrollments${qs(subject as Params)}`),
  enroll: (playbook: string, subject: Subject) =>
    request<{ id: number }>('/api/enrollments', { method: 'POST', body: { playbook, ...subject } }),
  // drip campaigns — a workflow run for a group, paced, with stop rules
  drips: () => request<{ drips: Drip[]; campaigns: { campaign: string; leads: number }[]; messaging: MessagingStatus }>('/api/drips'),
  drip: (id: number) => request<Drip>(`/api/drips/${id}`),
  previewDrip: (audience: DripAudience, members: number[], daily_limit?: number, pace?: DripPace) =>
    request<DripPreview>('/api/drips/preview', { method: 'POST', body: { audience, members, daily_limit, pace } }),
  createDrip: (d: DripPayload) => request<Drip>('/api/drips', { method: 'POST', body: d }),
  updateDrip: (id: number, d: DripPayload) => request<Drip>(`/api/drips/${id}`, { method: 'PUT', body: d }),
  dripJourney: (id: number) => request<DripJourney>(`/api/drips/${id}/journey`),
  running: (opts: { everyone?: boolean; attention?: boolean } = {}) =>
    request<{ running: RunningItem[] }>(`/api/running${qs(opts)}`),
  launchDrip: (id: number) => request<Drip>(`/api/drips/${id}/launch`, { method: 'POST', body: {} }),
  setDripStatus: (id: number, status: 'active' | 'paused' | 'stopped') =>
    request<Drip>(`/api/drips/${id}`, { method: 'PATCH', body: { status } }),
  addToDrip: (id: number, contactIds: number[]) =>
    request<Drip>(`/api/drips/${id}/members`, { method: 'POST', body: { contact_ids: contactIds } }),

  // workflows officers build themselves
  workflows: () => request<WorkflowsResponse>('/api/workflows'),
  /** Draft from a plain-English description. Slow (10–20s): it calls Claude. */
  draftWorkflow: (description: string) =>
    request<WorkflowDraftResult>('/api/workflows/draft', { method: 'POST', body: { description } }),
  createWorkflow: (w: WorkflowDraft) =>
    request<Playbook>('/api/workflows', { method: 'POST', body: w }),
  updateWorkflow: (id: number, w: WorkflowDraft) =>
    request<Playbook>(`/api/workflows/${id}`, { method: 'PUT', body: w }),
  deleteWorkflow: (id: number) =>
    request<{ ok: boolean }>(`/api/workflows/${id}`, { method: 'DELETE', body: {} }),
  enrollment: (id: number) => request<EnrollmentDetail>(`/api/enrollments/${id}`),
  /** Skip the rest of the current wait and run the next steps now. */
  runEnrollmentNow: (id: number) =>
    request<EnrollmentDetail>(`/api/enrollments/${id}/run-now`, { method: 'POST', body: {} }),
  setEnrollment: (id: number, status: 'active' | 'paused' | 'stopped') =>
    request<{ ok: boolean }>(`/api/enrollments/${id}`, { method: 'PATCH', body: { status } }),

  // calling
  callingStatus: () => request<CallingStatus>('/api/calling/status'),
  dialerToken: () => request<{ identity: string; token: string }>('/api/dialer/token'),
  callingQueue: (ownerLoId?: number) =>
    request<CallingQueue>(`/api/calling/queue${qs({ owner_lo_id: ownerLoId })}`),
  startCall: (contactId: number) =>
    request<CallResult>('/api/calls', { method: 'POST', body: { contact_id: contactId } }),
  disposeCall: (callId: number, body: { disposition: string; notes?: string; callback_at?: string }) =>
    request<{ status: string }>(`/api/calls/${callId}/disposition`, { method: 'POST', body }),
  transferCall: (callId: number, loId?: number) =>
    request<{ status: string; lo: string; lo_id: number; simulated: boolean }>(
      `/api/calls/${callId}/transfer`, { method: 'POST', body: { lo_id: loId } }),
  cancelTransfer: (callId: number) =>
    request<{ status: string }>(`/api/calls/${callId}/transfer/cancel`, { method: 'POST', body: {} }),
  recordCall: (callId: number, affirmation: string) =>
    request<{ status: string }>(`/api/calls/${callId}/recording`, { method: 'POST', body: { affirmation } }),

  // partners
  partners: (group = 'agents') =>
    request<{ partners: Partner[]; group: string; groups: string[] }>(`/api/partners${qs({ group })}`),
  partnerReport: (key: string, days = 90) =>
    request<{ rows: PartnerReportRow[]; days: number }>(`/api/partners/${enc(key)}/report${qs({ days })}`),
  sendPartnerReport: (key: string, days = 90) =>
    request<SendResult>(`/api/partners/${enc(key)}/report/send`, { method: 'POST', body: { days } }),
}
